import { propertyMedia } from '@/lib/property-media';
import { PropertyMediaHero } from '@/components/property/property-media';
import { useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useBooking } from "@/hooks/use-booking-context";
import { OtherHomeSwitch } from "@/components/property/other-home-switch";
import { KUALOA_PROPERTY_SLUG, KUALOA_STAY_PATH } from "@shared/property-context";
import { Header } from "@/components/layout/header";
import { SectionNav } from "@/components/property/section-nav";
import { Footer } from "@/components/layout/footer";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { FaqSection } from "@/components/seo/faq-section";
import { BookingWithOffers } from "@/components/promotions/booking-with-offers";

import { WhyBookDirect } from "@/components/booking/why-book-direct";
import { BookingOfferPopup } from "@/components/booking/booking-offer-popup";
import { PhotoGallery } from "@/components/property/photo-gallery";
import { AmenitiesGrid } from "@/components/property/amenities-grid";
import { LocationMap } from "@/components/property/location-map";
import ReviewsSection from "@/components/property/reviews-section";
import { SectionHeading } from "@/components/ui/section-heading";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Bed, Bath, Star, Shield, Key, Calendar, Droplets } from "lucide-react";
import { useSEO, useSEOSettings, generateBreadcrumbSchema, generateReviewsSchema, generateFAQSchema, generateKnowledgeGraphSchema, generateSpeakableSchema, LOCATION_KEYWORDS, type SiteInfoSettings } from "@/lib/seo-utils";
import { formatTimeToStandard } from "@/lib/time-utils";
import { attractionSlug } from "@shared/slug-utils";
import type { PublicProperty, NearbyAttraction } from "@shared/schema";
import { BOOK_DIRECT_SAVINGS, QUIET_HOURS_START, QUIET_HOURS_END } from "@shared/property-facts";

const propertyFAQs = [
  {
    question: "Where is the Oahu vacation rental located?",
    answer: "Our beachfront beach house is located in Ka'a'awa on Oahu's scenic windward coast, directly across from Kualoa Ranch (Jurassic Park filming location). It's about 23 miles (30 minutes) from Honolulu International Airport."
  },
  {
    question: "What amenities are included?",
    answer: "The property includes a full kitchen, high-speed WiFi, air conditioning, free parking, washer/dryer, beach gear, and outdoor entertaining space with ocean views. All linens and towels are provided."
  },
  {
    question: "Is the property actually oceanfront?",
    answer: "Yes! The property is directly oceanfront with panoramic Pacific Ocean views. You can see Mokoli'i (Chinaman's Hat) from the property, and there's private beach access."
  },
  {
    question: "What nearby attractions can guests visit?",
    answer: "Guests can walk to Kualoa Ranch, kayak to Mokoli'i (Chinaman's Hat), visit the Polynesian Cultural Center (15 min drive), explore North Shore beaches (30 min), or hike numerous windward coast trails."
  },
  {
    question: "Is the vacation rental suitable for families?",
    answer: "Absolutely! Our 3-bedroom beach house is perfect for families with children. The calm waters nearby are great for kids, and Kualoa Ranch offers family-friendly activities across the street."
  },
  {
    question: "Why book this Oahu beach house direct instead of Airbnb?",
    answer: `${BOOK_DIRECT_SAVINGS} Payments are processed securely through Stripe, and you receive instant confirmation from the property.`
  }
];

export default function StayPage() {
  const [location] = useLocation();
  const { setProperty } = useBooking();
  const { data: property, isLoading } = useQuery<PublicProperty>({
    queryKey: ["/api/properties", KUALOA_PROPERTY_SLUG],
  });

  useEffect(() => {
    if (property?.id && property.slug) setProperty(property.id, property.slug);
  }, [property?.id, property?.slug, setProperty]);

  // Get reviews for structured data
  const { data: reviews = [] } = useQuery<any[]>({
    queryKey: ["/api/reviews/public"],
  });

  // Get actual review stats for dynamic display
  const { data: reviewSummary } = useQuery<{ averageRating: number; totalReviews: number }>({
    queryKey: ["/api/reviews/summary"],
  });

  // Get SEO settings for schema generation
  const { seoSettings } = useSEOSettings();

  // Get site settings for dynamic schema content
  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ['/api/settings/site_info'],
    retry: false,
  });

  const { data: attractions = [] } = useQuery<NearbyAttraction[]>({
    queryKey: ["/api/nearby-attractions"],
  });

  const seoHead = useSEO({
    pageType: "stay",
    // Static, keyword-led title. The property's own name is the H1 on this page, so it
    // does not need to consume the limited title budget.
    title: "Oahu Beach House Rental in Ka'a'awa | The Kualoa Beach House",
    keywords: `${LOCATION_KEYWORDS.stayPrimary}, ${LOCATION_KEYWORDS.locations}, ${LOCATION_KEYWORDS.amenities}, 3 bedroom beach house oahu, beach house features, beach house amenities, private beach access oahu`,
    ogType: "place",
    ogTitle: "Oahu Beach House Rental | Beachfront Oceanfront Home in Ka'a'awa",
    ogDescription: "Our Oahu beach house rental: a 3-bedroom beachfront oceanfront home in Ka'a'awa with private beach access, photos, and amenities.",
    ogImage: "https://vacationrentaloahu.co/images/og-sharing-image.jpg",
    twitterCard: "summary_large_image",
    canonical: `https://vacationrentaloahu.co${KUALOA_STAY_PATH}`,
    structuredData: seoSettings && property ? [
      generateKnowledgeGraphSchema({ property, seoSettings, siteInfoSettings: siteSettings }),
      ...generateReviewsSchema(reviews, property, seoSettings),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Property Details", url: KUALOA_STAY_PATH }
      ]),
      generateFAQSchema(propertyFAQs),
      generateSpeakableSchema(["h1", ".faq-answer"]),
    ] : []
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-shell">
        <Header />
        <div className="pt-28 pb-16">
          <div className="container mx-auto px-4">
            <Skeleton className="h-[40vh] w-full mb-8" />
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2 space-y-8">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-24 w-full" />
                <div className="grid grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-16" />
                  ))}
                </div>
              </div>
              <div>
                <Skeleton className="h-96 w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    {seoHead}
    <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
      <Header />

      <main id="main-content" className="has-section-nav">
        <PropertyMediaHero media={propertyMedia(KUALOA_PROPERTY_SLUG)!} title={property?.title || 'The Kualoa Beach House'} location={property?.general_location || "Ka'a'awa, Oahu"} />

        <section className="bg-shell border-b border-sand-deep">
          <div className="container mx-auto px-4 md:px-6 py-6 md:py-8 max-w-6xl">
            <Breadcrumbs
              items={[{ name: "Home", url: "/" }, { name: "Property Details", url: KUALOA_STAY_PATH }]}
              className="mb-5"
            />
            <div className="flex flex-wrap items-center gap-3 text-luxury-sm">
              <div className="flex items-center">
                <Star className="h-4 w-4 text-sun fill-current mr-1 drop-shadow-none" aria-hidden="true" />
                <span className="font-serif font-normal mr-1 text-ink" data-testid="property-rating">
                  {reviewSummary && reviewSummary.totalReviews > 0 ? reviewSummary.averageRating.toFixed(2) : 'New'}
                </span>
                <span className="text-ink-soft">
                  {reviewSummary && reviewSummary.totalReviews > 0 ? `(${reviewSummary.totalReviews} reviews)` : ''}
                </span>
              </div>
              <span className="text-sand-deep" aria-hidden="true">•</span>
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-driftwood">
                Superhost
              </span>
              <span className="text-sand-deep" aria-hidden="true">•</span>
              <span className="text-ink-soft" data-testid="property-location">
                {property?.general_location || "Ka'a'awa, Oahu"}
              </span>
            </div>
          </div>
        </section>

        {/* Photo Gallery */}
        <SectionNav
          items={[
            { id: "photos", label: "Photos" },
            { id: "overview", label: "Overview" },
            { id: "amenities", label: "Amenities" },
            { id: "location", label: "Location" },
            { id: "reviews", label: "Reviews" },
            { id: "faq", label: "FAQ" },
            { id: "book", label: "Book" },
          ]}
        />

        <div id="photos">
          <PhotoGallery propertySlug={KUALOA_PROPERTY_SLUG} />
        </div>


        {/* Main Content */}
        <section id="overview" className="pt-6 pb-12 md:py-16 bg-shell section-stable border-t border-sand-deep">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
                <div className="lg:col-span-2 order-2 lg:order-1">
                  {/* Property Overview */}
                  <div className="mb-10">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <SectionHeading
                          align="left"
                          eyebrow="Entire Home"
                          title="Oahu beach house in Ka'a'awa"
                          className="mb-2"
                        />
                        <div className="flex items-center space-x-4 text-ink-soft text-luxury-sm font-sans tracking-wide">
                          <span data-testid="property-guests-overview">
                            {property?.max_guests || '8'} GUESTS
                          </span>
                          <span className="text-sand-deep">•</span>
                          <span data-testid="property-bedrooms-overview">
                            {property?.bedrooms || '4'} BEDROOMS
                          </span>
                          <span className="text-sand-deep">•</span>
                          <span data-testid="property-bathrooms-overview">
                            {property?.bathrooms || '3'} BATHROOMS
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-ink-soft leading-relaxed mt-6 max-w-3xl text-luxury-lg font-light">
                      This Oahu beach house rental sits right on the sand in Ka'a'awa on Oahu's windward coast, one of the island's most beautiful and least crowded stretches of beachfront. The entire oceanfront home is yours, with private beach access, ocean views, and premium amenities, just minutes from Kualoa Ranch and Mokoli'i (Chinaman's Hat). Book direct with no platform fees.
                    </p>
                  </div>

                  {/* Key Features */}
                  <div className="property-editorial-grid mb-12">
                    <div className="flex items-start space-x-5">
                      <div className="w-10 h-10 rounded-full bg-palm/10 flex items-center justify-center flex-shrink-0">
                        <Shield className="h-5 w-5 text-palm stroke-[1.5]" />
                      </div>
                      <div>
                        <h3 className="text-luxury-lg font-serif font-normal text-forest mb-1 tracking-tight">Entire beach house</h3>
                        <p className="text-ink-soft text-luxury-sm font-light leading-relaxed">
                          You'll have the entire Beach House Oahu property to yourself with beach access and premium amenities.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-5">
                      <div className="w-10 h-10 rounded-full bg-palm/10 flex items-center justify-center flex-shrink-0">
                        <Droplets className="h-5 w-5 text-palm stroke-[1.5]" />
                      </div>
                      <div>
                        <h3 className="text-luxury-lg font-serif font-normal text-forest mb-1 tracking-tight">Enhanced Clean</h3>
                        <p className="text-ink-soft text-luxury-sm font-light leading-relaxed">
                          Professional 5-step enhanced cleaning process for your safety.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-5">
                      <div className="w-10 h-10 rounded-full bg-palm/10 flex items-center justify-center flex-shrink-0">
                        <Key className="h-5 w-5 text-palm stroke-[1.5]" />
                      </div>
                      <div>
                        <h3 className="text-luxury-lg font-serif font-normal text-forest mb-1 tracking-tight">Self check-in</h3>
                        <p className="text-ink-soft text-luxury-sm font-light leading-relaxed">
                          Check yourself in with the smart lock and start your vacation immediately.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-5">
                      <div className="w-10 h-10 rounded-full bg-palm/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-palm stroke-[1.5]" />
                      </div>
                      <div>
                        <h3 className="text-luxury-lg font-serif font-normal text-forest mb-1 tracking-tight">Cancellation policy</h3>
                        <ul className="text-ink-soft text-luxury-sm font-light leading-relaxed space-y-0.5 list-none">
                          <li>Full refund if cancelled 30 or more days before check-in</li>
                          <li>50% refund of nightly total 7–30 days before check-in</li>
                          <li>No nightly refund within 7 days; cleaning fee always refunded</li>
                        </ul>
                        <a href="/policies" className="text-luxury-xs text-driftwood hover:text-ink underline underline-offset-2 mt-1 inline-flex min-h-[24px] items-center font-sans">Full policy →</a>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-12 pb-12 border-b border-sand-deep">
                    <SectionHeading
                      align="left"
                      eyebrow="The Experience"
                      title="About the beach house"
                      className="mb-6"
                    />
                    <div className="space-y-5 text-ink-soft leading-relaxed font-light text-luxury-base" data-testid="property-description">
                      {siteSettings?.site_description ? (
                        <div dangerouslySetInnerHTML={{ __html: siteSettings.site_description }} />
                      ) : (
                        <>
                          <p>
                            Escape to our stunning Beach House Oahu property perched on the pristine shores of Oahu's most exclusive coastline. 
                            This architectural masterpiece seamlessly blends modern elegance with authentic Hawaiian charm, offering 
                            an unparalleled vacation experience for up to 8 guests.
                          </p>
                          <p>
                            <strong>Experience the Real Hawaii:</strong> Unlike crowded tourist areas, our beachfront sanctuary offers you the authentic island experience. 
                            Enjoy peaceful 2-mile beach walks without the crowds, immerse yourself in genuine Hawaiian culture, and live like a local in this 
                            pristine neighborhood. This is Hawaii the way it was meant to be experienced: tranquil, unspoiled, and real.
                          </p>
                          <p>
                            Wake up to breathtaking ocean views from every bedroom, enjoy the spacious outdoor living areas, 
                            or stroll down to your own stretch of white sand beach. The Beach House features four spacious bedrooms, each with 
                            en-suite bathrooms, a full kitchen, and expansive outdoor living spaces perfect 
                            for entertaining.
                          </p>
                          <p>
                            <strong>Best of Both Worlds:</strong> While you'll enjoy the peace and seclusion of beachfront living away from tourist crowds, 
                            you're just a short scenic drive from world-class dining, shopping, and adventure activities whenever you desire. 
                            This is more than accommodation. It's your gateway to experiencing the true soul of Hawaii with all the comfort and convenience you deserve.
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* House Rules */}
                  <div className="mb-10">
                    <SectionHeading
                      align="left"
                      eyebrow="Details"
                      title="House rules"
                      className="mb-8"
                    />
                    <div className="grid md:grid-cols-3 gap-6 mb-8 border border-sand-deep rounded p-6 bg-shell">
                      <div>
                        <h3 className="text-luxury-sm font-sans tracking-widest text-driftwood uppercase mb-2">Check-in</h3>
                        <p className="text-ink font-serif text-luxury-lg">
                          After {formatTimeToStandard(property?.check_in_time) || '3:00 PM'}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-luxury-sm font-sans tracking-widest text-driftwood uppercase mb-2">Check-out</h3>
                        <p className="text-ink font-serif text-luxury-lg">
                          Before {formatTimeToStandard(property?.check_out_time) || '10:00 AM'}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-luxury-sm font-sans tracking-widest text-driftwood uppercase mb-2">Maximum guests</h3>
                        {property?.max_guests ? (
                          <p className="text-ink font-serif text-luxury-lg">{property.max_guests} guests</p>
                        ) : (
                          <p className="text-ink font-serif text-luxury-lg">8 guests</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-3 text-ink-soft text-luxury-base font-light">
                      <p className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-sand-deep block"></span> No smoking anywhere on the property</p>
                      <p className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-sand-deep block"></span> No events or parties</p>
                      <p className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-sand-deep block"></span> No pets. Pets are not permitted</p>
                      <p className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-sand-deep block"></span> Quiet hours from {QUIET_HOURS_START} to {QUIET_HOURS_END}</p>
                    </div>
                  </div>
                </div>

                {/* Booking Widget: first on mobile so it sits under the photo gallery */}
                <div className="lg:col-span-1 order-1 lg:order-2">
                  <div id="book" className="sticky top-28 z-20" data-testid="booking-widget-container" data-sticky-book-anchor>
                    <BookingWithOffers propertyId={property?.id} propertySlug={KUALOA_PROPERTY_SLUG} />
                    <OtherHomeSwitch currentSlug={property?.slug || KUALOA_PROPERTY_SLUG} className="mt-6" />
                    <WhyBookDirect variant="compact" className="mt-6" />
                    
                    <div className="mt-8 p-6 border border-sand-deep rounded text-center bg-shell">
                      <p className="text-luxury-sm text-ink-soft mb-4 tracking-wide">
                        Questions about the property?
                      </p>
                      <Link href="/contact">
                        <Button 
                          variant="bronze" 
                          size="lg" 
                          data-testid="contact-host-button"
                          className="w-full"
                        >
                          Contact Host
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Amenities */}
        <AmenitiesGrid />

        {/* Location */}
        <LocationMap />

        {/* Reviews */}
        <div id="reviews">
          <ReviewsSection />
        </div>

        {/* The carousel above shows a selection. This is the path to the full,
            indexable set - without it the hub is orphaned from the one page a
            reader is on when they most want more evidence. */}
        <section className="bg-shell border-t border-sand-deep" aria-label="More guest reviews">
          <div className="container mx-auto px-5 md:px-8 max-w-[1040px] py-8 md:py-10 text-center">
            <p className="text-[15px] md:text-base text-ink-soft font-sans font-light leading-relaxed">
              Want to read every one?{" "}
              <Link
                href="/reviews"
                className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300"
                data-testid="stay-all-reviews-link"
              >
                See all guest reviews
              </Link>{" "}
              for the complete set, grouped by families, groups and couples.
            </p>
          </div>
        </section>

        {/* Ka'a'awa & nearby */}
        <section className="py-20 bg-shell border-t border-sand-deep">
          <div className="container mx-auto px-4 max-w-6xl">
            <SectionHeading
              align="center"
              eyebrow="Location"
              title="Ka'a'awa & windward Oahu"
              className="mb-8"
            />
            <p className="text-ink-soft mb-10 max-w-2xl mx-auto text-center leading-relaxed font-light text-luxury-lg">
              Our beachfront home sits on Oahu's scenic windward coast: walk to Kualoa Ranch, kayak toward Mokoli'i (Chinaman's Hat),
              and use our <Link href="/kaaawa" className="text-ink underline decoration-sand-deep underline-offset-4 hover:text-ink/70">Ka'a'awa area guide</Link> to plan day trips.
            </p>
            {attractions.length > 0 && (
              <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-8 mb-12 max-w-3xl mx-auto text-center text-luxury-base font-serif">
                {attractions.slice(0, 6).map((a) => (
                  <li key={a.id}>
                    <Link href={`/nearby/${attractionSlug(a.name)}`} className="inline-flex min-h-[24px] items-center text-ink hover:text-ink-soft transition-colors underline decoration-sand-deep underline-offset-4">
                      {a.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <div className="text-center space-x-4">
              <Link href="/blog">
                <Button variant="outline" className="border-sand-deep text-ink hover:bg-sand hover:text-ink font-sans tracking-wide">
                  Oahu Travel Guide
                </Button>
              </Link>
              <Link href={`${KUALOA_STAY_PATH}/book`}>
                <Button className="bg-ink text-shell hover:bg-ink/90 font-sans tracking-wide">
                  Check Availability
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <div id="faq">
          <FaqSection eyebrow="Help & Info" title="Property FAQ" items={propertyFAQs} />
        </div>
      </main>

      <Footer />
      <StickyMobileBookCta />
      <BookingOfferPopup autoTrigger={false} />
    </div>
    </>
  );
}
